import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { MapPin } from "lucide-react";

export default function Place() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/place/${encodeURIComponent(key)}`).then(r=>r.json()).then(setData); }, [key]);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const weeks = Object.keys(data.demand_density || {}).sort();
  return (
    <div className="space-y-6" data-testid="place-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><MapPin className="w-3.5 h-3.5"/>Place</div>
        <h1 className="text-3xl font-bold">{data.key}</h1>
        <p className="text-sm text-slate-500 mt-1">{data.kind === "zip" ? "ZIP" : "Place"} · programs, properties, and demand observed here</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 bg-white" data-testid="place-programs">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Programs operating here ({data.programs.length})</div>
          {data.programs.length === 0 ? <div className="text-xs text-slate-500 italic">None recorded.</div> : (
            <ul className="space-y-1.5 text-sm">
              {data.programs.map(p => <li key={p.id}><Link to={`/programs/${p.id}`} className="hover:text-[#C25E00]">{p.name}</Link> <span className="text-xs text-slate-500 capitalize">· {(p.vertical||"").replace("_"," ")}</span></li>)}
            </ul>
          )}
        </Card>
        <Card className="p-5 border-slate-200 bg-white" data-testid="place-properties">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Properties in play ({data.properties.length})</div>
          {data.properties.length === 0 ? <div className="text-xs text-slate-500 italic">No vetted properties recorded here.</div> : (
            <ul className="space-y-1.5 text-sm">
              {data.properties.map(p => <li key={p.id}><Link to={`/properties/${p.id}`} className="hover:text-[#C25E00]">{p.address}</Link> <span className="text-xs text-slate-500">· {p.bedrooms}BR/{p.bathrooms}BA</span></li>)}
            </ul>
          )}
        </Card>
      </div>
      <Card className="p-5 border-slate-200 bg-white" data-testid="place-density">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Anonymous demand signal — recent weeks</div>
        {weeks.length === 0 ? <div className="text-xs text-slate-500 italic">No search activity recorded yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-2 py-1">Week</th><th className="text-left px-2 py-1">Signals by situation</th></tr>
              </thead>
              <tbody>
                {weeks.map(w => (
                  <tr key={w} className="border-t border-slate-100">
                    <td className="px-2 py-2 font-mono text-xs">{w}</td>
                    <td className="px-2 py-2 text-xs">{Object.entries(data.demand_density[w]).map(([s,n]) => <span key={s} className="inline-block mr-2 px-2 py-0.5 bg-slate-100 rounded">{s}: {n}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[10px] text-slate-500 italic mt-2">{data.note}</div>
      </Card>
    </div>
  );
}
